import { createContext, useCallback, useContext, useEffect, useState, ReactNode } from 'react';
import { api, setAccessToken, setRefreshToken, User } from './api';

type AuthCtx = {
  user: User | null;
  loading: boolean;
  login: (identifier: string, password: string) => Promise<User>;
  logout: () => Promise<void>;
  refreshMe: () => Promise<void>;
};

const Ctx = createContext<AuthCtx | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const refreshMe = useCallback(async () => {
    try { setUser(await api.get<User>('/me')); } catch { setUser(null); }
  }, []);

  useEffect(() => {
    (async () => {
      const ok = await api.tryRefresh();
      if (ok) await refreshMe();
      setLoading(false);
    })();
  }, [refreshMe]);

  const login = useCallback(async (identifier: string, password: string) => {
    const r = await api.post<{ accessToken: string; refreshToken?: string; user: Pick<User, 'id'|'email'|'username'> }>(
      '/auth/login', { identifier, password }
    );
    setAccessToken(r.accessToken);
    // Persist the refresh token so the session survives PWA / in-app-
    // webview cookie eviction (the logout-on-reopen bug). Harmless when
    // the cookie also works — the server prefers the cookie.
    if (r.refreshToken) setRefreshToken(r.refreshToken);
    const u = await api.get<User>('/me');
    setUser(u);
    return u;
  }, []);

  const logout = useCallback(async () => {
    // api.logout() revokes the server session AND clears both the in-memory
    // access token and the persisted refresh token.
    try { await api.logout(); } catch { /* noop */ }
    setUser(null);
  }, []);

  return <Ctx.Provider value={{ user, loading, login, logout, refreshMe }}>{children}</Ctx.Provider>;
}

export function useAuth() {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAuth must be used inside <AuthProvider>');
  return v;
}
